import { getSignedUrlBase64 } from "@/api/auth/gcs";

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Resolve a tenant logo (GCS path or absolute URL) to a data URL.
 *
 * Shared by every document we render in the browser — the fax cover sheet, and
 * the faxed copy of a note — because both are read back out of a canvas or a
 * PDF renderer that cannot touch a cross-origin image. The app's GCS bucket
 * isn't CORS-enabled for the app origin, so stored logos are pulled through the
 * backend as a base64 data URI instead of fetched directly.
 *
 * Returns null on any failure: a document must never fail on its logo, it
 * renders text-only instead (mirroring the backend's broken-logo fallback).
 */
export async function resolveLogoDataUrl(
  logoUrl: string | null | undefined,
  signal?: AbortSignal,
): Promise<string | null> {
  if (!logoUrl) return null;
  try {
    if (!/^https?:\/\//i.test(logoUrl)) {
      const signed = await getSignedUrlBase64(logoUrl);
      return signed?.signedUrl ?? null;
    }
    const res = await fetch(logoUrl, { signal });
    if (!res.ok) return null;
    return await blobToDataUrl(await res.blob());
  } catch {
    return null;
  }
}
